export type AdminNavigationIcon =
  | 'dashboard'
  | 'users'
  | 'projects'
  | 'reports'
  | 'reviews'
  | 'websiteContent'
  | 'serviceCatalog'
  | 'campaigns'
  | 'blog'
  | 'services'
  | 'industries'
  | 'cities'
  | 'credits'
  | 'administrator'
  | 'userLookup';

type AdminRouteMatcher = {
  exactPaths?: string[];
  pathPrefixes?: string[];
};

export type AdminNavigationSubItem = AdminRouteMatcher & {
  name: string;
  path: string;
};

export type AdminNavigationItem = AdminRouteMatcher & {
  name: string;
  icon: AdminNavigationIcon;
  path?: string;
  subItems?: AdminNavigationSubItem[];
};

export type AdminNavigationSection = {
  name: string | null;
  items: AdminNavigationItem[];
};

export const adminNavigationSections: AdminNavigationSection[] = [
  {
    name: null,
    items: [
      {
        name: 'dashboard',
        icon: 'dashboard',
        path: '/admin',
        exactPaths: ['/admin'],
      },
    ],
  },
  {
    name: 'platform',
    items: [
      {
        name: 'profiles',
        icon: 'users',
        path: '/admin/users',
        exactPaths: ['/admin/users', '/admin/freelancers'],
        pathPrefixes: ['/admin/freelancers/'],
      },
      {
        name: 'projects',
        icon: 'projects',
        path: '/admin/jobs',
        exactPaths: ['/admin/jobs'],
        pathPrefixes: ['/admin/jobs/'],
      },
    ],
  },
  {
    name: 'moderation',
    items: [
      {
        name: 'reports',
        icon: 'reports',
        path: '/admin/reports',
        exactPaths: ['/admin/reports'],
      },
      {
        name: 'reviews',
        icon: 'reviews',
        path: '/admin/reviews',
        exactPaths: ['/admin/reviews'],
      },
    ],
  },
  {
    name: 'website',
    items: [
      {
        name: 'websiteContent',
        icon: 'websiteContent',
        path: '/admin/website-content',
        exactPaths: ['/admin/website-content', '/admin/website-media'],
      },
      {
        name: 'serviceCatalog',
        icon: 'serviceCatalog',
        path: '/admin/service-catalog',
        exactPaths: ['/admin/service-catalog'],
      },
      {
        name: 'campaigns',
        icon: 'campaigns',
        path: '/admin/campaigns',
        exactPaths: ['/admin/campaigns'],
      },
      {
        name: 'blog',
        icon: 'blog',
        pathPrefixes: ['/admin/blog'],
        subItems: [
          {
            name: 'articles',
            path: '/admin/blog',
            exactPaths: ['/admin/blog'],
            pathPrefixes: ['/admin/blog/'],
          },
          {
            name: 'categories',
            path: '/admin/blog/categories',
            exactPaths: ['/admin/blog/categories'],
          },
          {
            name: 'tags',
            path: '/admin/blog/tags',
            exactPaths: ['/admin/blog/tags'],
          },
        ],
      },
    ],
  },
  {
    name: 'configuration',
    items: [
      {
        name: 'services',
        icon: 'services',
        path: '/admin/services',
        exactPaths: ['/admin/services'],
      },
      {
        name: 'industries',
        icon: 'industries',
        path: '/admin/industries',
        exactPaths: ['/admin/industries'],
      },
      {
        name: 'cities',
        icon: 'cities',
        path: '/admin/cities',
        exactPaths: ['/admin/cities'],
      },
      {
        name: 'credits',
        icon: 'credits',
        path: '/admin/credits',
        exactPaths: ['/admin/credits'],
      },
      {
        name: 'creditTransactions',
        icon: 'credits',
        path: '/admin/credit-transactions',
        exactPaths: ['/admin/credit-transactions'],
      },
    ],
  },
  {
    name: 'administration',
    items: [
      {
        name: 'create',
        icon: 'administrator',
        path: '/admin/create',
        exactPaths: ['/admin/create'],
      },
      {
        name: 'userLookup',
        icon: 'userLookup',
        path: '/admin/user-lookup',
        exactPaths: ['/admin/user-lookup'],
      },
    ],
  },
];

const matchesRoute = (pathname: string, matcher: AdminRouteMatcher): boolean => {
  if (matcher.exactPaths?.includes(pathname)) {
    return true;
  }

  return matcher.pathPrefixes?.some(prefix => pathname.startsWith(prefix)) ?? false;
};

export const findMatchingAdminSubItem = (
  pathname: string,
  item: AdminNavigationItem
): AdminNavigationSubItem | null => {
  const subItems = item.subItems ?? [];
  const exactMatch = subItems.find(subItem => subItem.exactPaths?.includes(pathname));

  return exactMatch ?? subItems.find(subItem => matchesRoute(pathname, subItem)) ?? null;
};

export const isAdminNavigationItemActive = (pathname: string, item: AdminNavigationItem): boolean =>
  matchesRoute(pathname, item);

export const findActiveAdminNavigationItem = (
  pathname: string
): { itemName: string; subItemName: string | null } | null => {
  for (const section of adminNavigationSections) {
    for (const item of section.items) {
      if (isAdminNavigationItemActive(pathname, item)) {
        return {
          itemName: item.name,
          subItemName: findMatchingAdminSubItem(pathname, item)?.name ?? null,
        };
      }
    }
  }

  return null;
};
